/**
 * 经典橙色模板 - 详情页
 * 
 * 设计风格（与 Modern 模板完全不同）：
 * - 大图展示区（非轮播，大图 + 下方缩略图行）
 * - 双栏布局：主内容左侧 + 信息卡右侧
 * - 白色信息卡，橙色点缀装饰
 * - 底部相关推荐：横向列表卡片（图左文右，与首页风格统一）
 * - 暖色系背景
 */

import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import {
  getListingDetail,
  getListings,
  type ListingItem,
} from "@/utils/api";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

export default function ClassicListingDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { siteId, loading: siteLoading } = useSite();
  const { siteConfig } = useSiteConfig();

  const [listing, setListing] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [relatedListings, setRelatedListings] = useState<ListingItem[]>([]);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showContact, setShowContact] = useState(false);
  const [favorites, setFavorites] = useState(false);

  // 加载详情
  useEffect(() => {
    if (!id || siteLoading || !siteId) return;
    setLoading(true);
    getListingDetail(id)
      .then((data) => setListing(data))
      .catch(() => navigate("/category", { replace: true }))
      .finally(() => setLoading(false));
  }, [id, siteId, siteLoading]);

  // 加载相关推荐
  useEffect(() => {
    if (loading || !listing || !siteId) return;
    getListings({ page_size: 6 })
      .then((data) => {
        const filtered = (data.list || []).filter(l => l.id !== id).slice(0, 4);
        setRelatedListings(filtered);
      })
      .catch(console.error);
  }, [loading, listing, id, siteId]);

  if (loading) return <LoadingSkeleton />;
  if (!listing) return null;

  const images = listing.images && listing.images.length > 0 ? listing.images : [];

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Navbar />

      {/* ── 面包屑 ── */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-3 flex items-center gap-1.5 text-sm">
          <Link to="/" className="text-gray-400 hover:text-orange-600 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line text-xs text-gray-300" />
          <Link to="/category" className="text-gray-400 hover:text-orange-600 transition-colors">分类</Link>
          {listing.category_name && (
            <>
              <i className="ri-arrow-right-s-line text-xs text-gray-300" />
              <span className="text-gray-400 line-clamp-1">{listing.category_name}</span>
            </>
          )}
          <i className="ri-arrow-right-s-line text-xs text-gray-300" />
          <span className="text-gray-700 font-medium truncate">{listing.title}</span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* ════════ 左侧主内容区 ════════ */}
          <div className="flex-1 min-w-0 space-y-6">

            {/* ── 图片展示区 ── */}
            <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
              {/* 主图 */}
              <div className="relative w-full h-[320px] md:h-[450px] bg-gray-100">
                {images.length > 0 ? (
                  <img
                    src={images[activeImageIndex]}
                    alt={listing.title}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-gray-300">
                    <i className="ri-image-line text-6xl" />
                    <span className="mt-2 text-sm">暂无图片</span>
                  </div>
                )}

                {/* 图片计数器 */}
                {images.length > 1 && (
                  <span className="absolute top-3 right-3 px-2.5 py-1 bg-black/50 backdrop-blur-sm text-white text-xs rounded-full">
                    {activeImageIndex + 1} / {images.length}
                  </span>
                )}
              </div>

              {/* 缩略图行 */}
              {images.length > 1 && (
                <div className="flex gap-2 p-3 overflow-x-auto">
                  {images.map((img: string, i: number) => (
                    <button
                      key={i}
                      onClick={() => setActiveImageIndex(i)}
                      className={`w-16 h-16 md:w-20 md:h-20 shrink-0 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                        activeImageIndex === i
                          ? "border-orange-500 shadow-sm"
                          : "border-gray-100 opacity-70 hover:opacity-100 hover:border-orange-300"
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ── 标题 + 元数据 ── */}
            <div className="bg-white rounded-2xl p-5 md:p-6 border border-gray-100">
              <div className="flex items-start justify-between gap-3 mb-4">
                <h1 className="text-xl md:text-2xl font-bold text-gray-800 leading-tight">
                  {listing.title}
                </h1>
                {listing.is_pinned && (
                  <span className="shrink-0 px-2.5 py-1 bg-orange-500 text-white text-xs font-semibold rounded-lg">
                    置顶
                  </span>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-500 pb-5 border-b border-gray-50">
                {listing.category_name && (
                  <span className="px-2.5 py-1 bg-orange-50 text-orange-600 rounded-lg text-xs font-medium">
                    {listing.category_name}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <i className="ri-time-line" />
                  {new Date(listing.created_at).toLocaleDateString('zh-CN')}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line" />
                  {listing.view_count} 次浏览
                </span>
                <button
                  onClick={() => setFavorites(!favorites)}
                  className={`flex items-center gap-1 cursor-pointer ${favorites ? "text-orange-500" : "text-gray-400 hover:text-orange-500"} transition-colors`}
                >
                  <i className={`${favorites ? "ri-heart-fill" : "ri-heart-line"}`} />
                  收藏
                </button>
              </div>

              {/* ── 详细描述 ── */}
              <div className="mt-5">
                <h2 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="w-1 h-5 bg-orange-500 rounded-full" />
                  详细介绍
                </h2>
                <div className="text-gray-600 text-sm md:text-base leading-relaxed whitespace-pre-wrap">
                  {listing.content || "暂无描述信息"}
                </div>
              </div>
            </div>
          </div>

          {/* ════════ 右侧信息卡 ════════ */}
          <aside className="lg:w-80 shrink-0 space-y-5">
            {/* 联系人信息 */}
            <div className="bg-white rounded-2xl p-5 border border-gray-100">
              <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                <i className="ri-user-line text-orange-500" />
                联系人
              </h3>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center shrink-0">
                  <i className="ri-user-line text-xl text-orange-400" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-gray-800 text-sm truncate">
                    {listing.contact_name || '匿名用户'}
                  </p>
                  <p className="text-xs text-gray-400">联系人</p>
                </div>
              </div>
              {listing.company_name && !siteConfig?.hide_company_name && (
                <div className="mt-4 pt-3 border-t border-gray-50">
                  <p className="text-xs text-gray-400 mb-1">公司名称</p>
                  <p className="text-sm font-medium text-gray-700">{listing.company_name}</p>
                </div>
              )}
            </div>

            {/* 联系方式卡片 */}
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-5 text-white shadow-md">
              <h3 className="text-sm font-bold mb-4 flex items-center gap-2">
                <i className="ri-phone-line" />
                联系方式
              </h3>

              {!showContact ? (
                <button
                  onClick={() => setShowContact(true)}
                  className="w-full py-3 bg-white/20 hover:bg-white/30 rounded-xl font-medium text-sm transition-all cursor-pointer backdrop-blur-sm"
                >
                  点击查看联系方式
                </button>
              ) : (
                <div className="space-y-3">
                  {(listing.contact_phone || listing.contact_wechat) ? (
                    <>
                      {listing.contact_phone && (
                        <div className="flex items-center gap-3 py-2.5 px-3 bg-white/10 rounded-xl">
                          <i className="ri-phone-line text-white/80" />
                          <a href={`tel:${listing.contact_phone}`} className="text-sm font-medium">
                            {listing.contact_phone}
                          </a>
                        </div>
                      )}
                      {listing.contact_wechat && (
                        <div className="flex items-center gap-3 py-2.5 px-3 bg-white/10 rounded-xl">
                          <i className="ri-wechat-line text-green-200" />
                          <span className="text-sm font-medium">{listing.contact_wechat}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-white/50">暂无联系方式</p>
                  )}
                </div>
              )}
            </div>

            {/* 快速操作 */}
            <div className="bg-white rounded-2xl p-4 border border-gray-100">
              <Link
                to="/publish"
                className="flex items-center justify-center gap-2 w-full py-2.5 bg-[#8B4513] text-white text-sm font-medium rounded-xl hover:bg-[#6B3410] transition-colors cursor-pointer"
              >
                <i className="ri-add-line" />
                发布类似服务
              </Link>
            </div>
          </aside>
        </div>

        {/* ════════ 相关推荐 ════════ */}
        {relatedListings.length > 0 && (
          <section className="mt-12">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <span className="w-1 h-6 bg-orange-500 rounded-full" />
                相关推荐
              </h2>
              <Link
                to="/category"
                className="text-orange-600 text-sm font-medium hover:text-orange-700 transition-colors flex items-center gap-1"
              >
                查看更多
                <i className="ri-arrow-right-line" />
              </Link>
            </div>

            <div className="space-y-3 md:space-y-4">
              {relatedListings.map(item => (
                <RelatedCard key={item.id} item={item} />
              ))}
            </div>
          </section>
        )}
      </div>

      <Footer />
    </div>
  );
}

function RelatedCard({ item }: { item: ListingItem }) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <div
      ref={ref}
      className={`transition-all duration-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
    >
      <Link
        to={`/listing/${item.id}`}
        className="group flex gap-3 md:gap-4 p-3 md:p-4 bg-white rounded-2xl border border-gray-100 hover:border-orange-200 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
      >
        {/* 缩略图 */}
        <div className="w-28 h-24 md:w-32 md:h-24 shrink-0 rounded-xl overflow-hidden bg-gray-100">
          {item.images?.[0] ? (
            <img
              src={item.images[0]}
              alt={item.title}
              className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300">
              <i className="ri-image-line text-xl" />
            </div>
          )}
        </div>

        {/* 内容 */}
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <h3 className="text-sm md:text-base font-semibold text-gray-800 group-hover:text-orange-600 transition-colors line-clamp-1">
              {item.title}
            </h3>
            {item.category?.name && (
              <span className="inline-block px-2 py-0.5 bg-orange-50 text-orange-600 text-xs rounded-full mt-1.5">
                {item.category.name}
              </span>
            )}
            <p className="text-gray-500 text-xs mt-2 line-clamp-2 leading-relaxed">{item.content}</p>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-400 mt-2">
            <span>{new Date(item.created_at).toLocaleDateString('zh-CN')}</span>
            <span className="flex items-center gap-0.5"><i className="ri-eye-line" />{item.view_count}</span>
          </div>
        </div>
      </Link>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        <div className="animate-pulse space-y-6">
          {/* 大图骨架 */}
          <div className="w-full h-[400px] bg-gray-200 rounded-2xl" />
          <div className="flex gap-6">
            <div className="flex-1 space-y-4">
              <div className="h-8 bg-gray-200 rounded-lg w-3/4" />
              <div className="h-4 bg-gray-200 rounded w-1/2" />
              <div className="h-32 bg-gray-200 rounded-2xl" />
            </div>
            <div className="w-80 shrink-0 space-y-4">
              <div className="h-32 bg-gray-200 rounded-2xl" />
              <div className="h-40 bg-gray-200 rounded-2xl" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
